import React from 'react';
import { FileText, ShoppingBag, Video, MessageSquare, Clock, Download, Sparkles, BookOpen, ChevronRight, User as UserIcon } from 'lucide-react';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { useToast } from '../ui/Toast';
import { useLanguage } from '../../i18n/LanguageContext';
import { ZodiacTheme } from './zodiacThemes';
import { User } from '../../types';
interface OverviewTabProps {
  theme: ZodiacTheme;
  user: User | null;
  setActiveTab: (tab: string) => void;
}
const kpiItems = [{
  id: 'orders',
  label: 'Заказы',
  value: 2,
  Icon: ShoppingBag
}, {
  id: 'reports',
  label: 'Отчёты',
  value: 4,
  Icon: FileText
}, {
  id: 'consultations',
  label: 'Сессии',
  value: 1,
  Icon: Video
}, {
  id: 'chat',
  label: 'Сообщения',
  value: 3,
  Icon: MessageSquare,
  alert: true
}] as const;
const reports = [{
  title: 'Натальная карта',
  date: '12 Окт 2024',
  pending: false
}, {
  title: 'Соляр 2024',
  date: '05 Окт 2024',
  pending: false
}, {
  title: 'Совместимость',
  date: 'В процессе',
  pending: true
}];
const messages = [{
  name: 'Елена Смирнова',
  msg: 'Здравствуйте! Я посмотрела вашу карту...',
  time: '10:45',
  unread: 2
}, {
  name: 'Служба поддержки',
  msg: 'Ваш заказ успешно оплачен и передан...',
  time: 'Вчера',
  unread: 0
}, {
  name: 'Дмитрий Волков',
  msg: 'Спасибо за консультацию!',
  time: '12 Окт',
  unread: 0
}];
const purchases = [{
  title: 'Основы астрологии',
  type: 'Видеокурс',
  Icon: Video
}, {
  title: 'Лунные узлы и карма',
  type: 'PDF Гайд',
  Icon: BookOpen
}];
export function OverviewTab({
  theme,
  user,
  setActiveTab
}: OverviewTabProps) {
  const {
    showToast
  } = useToast();
  const {
    t
  } = useLanguage();
  const kpiItems = [{
    id: 'orders',
    label: t('userdash.overview.kpiOrders'),
    value: 2,
    Icon: ShoppingBag
  }, {
    id: 'reports',
    label: t('userdash.overview.kpiReports'),
    value: 4,
    Icon: FileText
  }, {
    id: 'consultations',
    label: t('userdash.overview.kpiSessions'),
    value: 1,
    Icon: Video
  }, {
    id: 'chat',
    label: t('userdash.overview.kpiMessages'),
    value: 3,
    Icon: MessageSquare,
    alert: true
  }] as const;
  const reports = [{
    title: t('userdash.overview.natalChart'),
    date: '12 Окт 2024',
    pending: false
  }, {
    title: t('userdash.overview.solar'),
    date: '05 Окт 2024',
    pending: false
  }, {
    title: t('userdash.overview.compatibility'),
    date: t('userdash.overview.inProcessDate'),
    pending: true
  }];
  const messages = [{
    name: 'Елена Смирнова',
    msg: 'Здравствуйте! Я посмотрела вашу карту...',
    time: '10:45',
    unread: 2
  }, {
    name: 'Служба поддержки',
    msg: 'Ваш заказ успешно оплачен и передан...',
    time: 'Вчера',
    unread: 0
  }, {
    name: 'Дмитрий Волков',
    msg: 'Спасибо за консультацию!',
    time: '12 Окт',
    unread: 0
  }];
  const purchases = [{
    title: t('userdash.overview.basicsAstrology'),
    type: t('userdash.overview.videoCourse'),
    Icon: Video
  }, {
    title: t('userdash.overview.lunarNodes'),
    type: t('userdash.overview.pdfGuide'),
    Icon: BookOpen
  }];
  const SectionHeader = ({
    title,
    tab,
    linkText = 'Все'
  }: {
    title: string;
    tab: string;
    linkText?: string;
  }) => <div className="flex justify-between items-center" data-id="element-18838">
      <h3 className="font-bold text-slate-900" data-id="element-18839">{title}</h3>
      <button onClick={() => setActiveTab(tab)} className={`text-sm font-medium ${theme.text} hover:underline flex items-center gap-0.5`} data-id="element-18840">
        {linkText} <ChevronRight className="w-4 h-4" data-id="element-18841" />
      </button>
    </div>;
  return <div className="space-y-6 pb-8" data-id="element-18842">
      {/* ── 1. Баннер ── */}
      <div className="bg-white border border-slate-200 rounded-2xl p-5 sm:p-6 shadow-sm relative overflow-hidden" data-id="element-18843">
        <div className="absolute top-0 right-0 -mt-6 -mr-6 text-[120px] opacity-5 pointer-events-none leading-none select-none" data-id="element-18844">
          {theme.icon}
        </div>
        <div className="relative z-10" data-id="element-18845">
          <span className={`inline-block bg-slate-100 px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider mb-3 ${theme.text}`} data-id="element-18846">
            {theme.name} • {theme.element}
          </span>
          <h2 className="text-xl sm:text-2xl font-bold text-slate-900 mb-1.5" data-id="element-18847">
            {t('userdash.overview.welcome', {
            name: user?.name?.split(' ')[0] || t('userdash.overview.guest')
          })}
          </h2>
          <p className="text-slate-500 text-sm mb-4 max-w-lg leading-relaxed" data-id="element-18848">
            {t('userdash.overview.todayMoon')}
          </p>
          <div className="flex gap-2.5" data-id="element-18849">
            <Button size="sm" onClick={() => showToast(t('userdash.overview.goToServices'))} data-id="element-18850">
              {t('userdash.overview.orderService')}
            </Button>
            <Button variant="outline" size="sm" onClick={() => setActiveTab('reports')} data-id="element-18851">
              {t('userdash.overview.myReports')}
            </Button>
          </div>
        </div>
      </div>

      {/* ── 2. KPI — единая полоса ── */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm" data-id="element-18852">
        <div className="flex overflow-x-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]" data-id="element-18853">
          {kpiItems.map((kpi, idx) => <button key={kpi.id} onClick={() => setActiveTab(kpi.id)} className={`flex-1 min-w-[120px] flex items-center gap-2.5 px-4 py-3 hover:bg-slate-50 transition-colors relative ${idx > 0 ? 'border-l border-slate-100' : ''}`} data-id="element-18854">
              {kpi.alert && <span className="absolute top-2 right-2 w-2 h-2 bg-rose-500 rounded-full" data-id="element-18855" />}
              <div className={`w-8 h-8 rounded-lg ${kpi.alert ? 'bg-rose-50 text-rose-600' : `${theme.bg} ${theme.text}`} flex items-center justify-center shrink-0`} data-id="element-18856">
                <kpi.Icon className="w-4 h-4" data-id="element-18857" />
              </div>
              <div className="text-left" data-id="element-18858">
                <div className="text-lg font-bold text-slate-900 leading-none" data-id="element-18859">
                  {kpi.value}
                </div>
                <div className="text-[11px] text-slate-500 font-medium mt-0.5" data-id="element-18860">
                  {kpi.label}
                </div>
              </div>
            </button>)}
        </div>
      </div>

      {/* ── 3. Ближайшая сессия ── */}
      <section className="space-y-2.5" data-id="element-18861">
        <SectionHeader title={t('userdash.overview.nextSession')} tab="consultations" data-id="element-18862" />
        <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm relative overflow-hidden" data-id="element-18863">
          <div className="absolute top-0 left-0 w-1 h-full bg-indigo-500 rounded-l-xl" data-id="element-18864" />
          <div className="pl-3" data-id="element-18865">
            <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3" data-id="element-18866">
              <div className="flex-1 min-w-0" data-id="element-18867">
                <div className="flex items-center gap-2 mb-1.5 flex-wrap" data-id="element-18868">
                  <Badge variant="pro" size="sm" className="bg-indigo-50 text-indigo-700" data-id="element-18869">
                    {t('userdash.overview.awaiting')}
                  </Badge>
                  <span className="text-xs text-slate-500 flex items-center gap-1" data-id="element-18870">
                    <Video className="w-3.5 h-3.5" data-id="element-18871" />{' '}
                    {t('userdash.overview.videoCall')}
                  </span>
                </div>
                <h4 className="font-bold text-slate-900 mb-1" data-id="element-18872">
                  {t('userdash.overview.synastry')}
                </h4>
                <div className="flex items-center gap-2 text-sm text-slate-600 mb-2" data-id="element-18873">
                  <div className="w-5 h-5 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-700 font-bold text-[9px] shrink-0" data-id="element-18874">
                    ЕС
                  </div>
                  <span data-id="element-18875">Елена Смирнова</span>
                </div>
                <div className="inline-flex items-center bg-amber-50 text-amber-800 text-xs font-medium px-2.5 py-1 rounded-md border border-amber-100" data-id="element-18876">
                  <Clock className="w-3.5 h-3.5 mr-1.5 text-amber-500" data-id="element-18877" />
                  {t('userdash.overview.tomorrow')}
                </div>
              </div>
              <div className="flex gap-2 w-full sm:w-auto shrink-0 pt-1" data-id="element-18878">
                <Button variant="outline" size="sm" className="flex-1 sm:flex-none" onClick={() => setActiveTab('chat')} data-id="element-18879">
                  <MessageSquare className="w-4 h-4 mr-1.5" data-id="element-18880" />{' '}
                  {t('userdash.overview.chatBtn')}
                </Button>
                <Button variant="primary" size="sm" className="flex-1 sm:flex-none bg-indigo-600 hover:bg-indigo-700" onClick={() => showToast(t('userdash.overview.waitForLink'))} data-id="element-18881">
                  {t('userdash.overview.joinCall')}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── 4. Последние отчёты ── */}
      <section className="space-y-2.5" data-id="element-18882">
        <SectionHeader title={t('userdash.overview.latestReports')} tab="reports" data-id="element-18883" />
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm divide-y divide-slate-100" data-id="element-18884">
          {reports.map((r, i) => <div key={i} className="flex items-center justify-between px-4 py-3 hover:bg-slate-50 transition-colors group cursor-pointer" onClick={() => setActiveTab('reports')} data-id="element-18885">
              <div className="flex items-center gap-3 min-w-0" data-id="element-18886">
                <div className={`w-9 h-9 rounded-lg ${r.pending ? 'bg-slate-100 text-slate-400' : `${theme.bg} ${theme.text}`} flex items-center justify-center shrink-0`} data-id="element-18887">
                  <FileText className="w-4 h-4" data-id="element-18888" />
                </div>
                <div className="min-w-0" data-id="element-18889">
                  <div className="font-semibold text-sm text-slate-900 truncate" data-id="element-18890">
                    {r.title}
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-1.5" data-id="element-18891">
                    {r.date}
                    {r.pending && <Badge variant="warning" size="sm" className="text-[9px] py-0 px-1 h-3.5 leading-none" data-id="element-18892">
                        {t('userdash.overview.inProgress')}
                      </Badge>}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-1.5 shrink-0 ml-2" data-id="element-18893">
                {!r.pending && <button className="p-1.5 text-slate-400 hover:text-slate-700 transition-colors hidden sm:block" title={t('userdash.overview.downloadPdf')} onClick={e => {
              e.stopPropagation();
              showToast(t('userdash.overview.downloading'));
            }} data-id="element-18894">
                    <Download className="w-4 h-4" data-id="element-18895" />
                  </button>}
                <Button variant={r.pending ? 'outline' : 'secondary'} size="sm" className="text-xs px-2.5 h-7" onClick={e => {
              e.stopPropagation();
              setActiveTab('reports');
            }} data-id="element-18896">
                  {t('userdash.overview.open')}
                </Button>
              </div>
            </div>)}
        </div>
      </section>

      {/* ── 5. Сообщения ── */}
      <section className="space-y-2.5" data-id="element-18897">
        <SectionHeader title={t('userdash.overview.messages')} tab="chat" data-id="element-18898" />
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm divide-y divide-slate-100" data-id="element-18899">
          {messages.map((m, i) => <div key={i} className={`flex items-center gap-3 px-4 py-3 hover:bg-slate-50 transition-colors cursor-pointer ${m.unread > 0 ? 'bg-indigo-50/30' : ''}`} onClick={() => setActiveTab('chat')} data-id="element-18900">
              <div className="relative shrink-0" data-id="element-18901">
                <div className="w-9 h-9 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-700 font-bold text-xs" data-id="element-18902">
                  {m.name.split(' ').map(n => n[0]).join('').substring(0, 2)}
                </div>
                {m.unread > 0 && <span className="absolute -top-0.5 -right-0.5 w-3.5 h-3.5 bg-rose-500 rounded-full border-2 border-white flex items-center justify-center text-[8px] font-bold text-white" data-id="element-18903">
                    {m.unread}
                  </span>}
              </div>
              <div className="flex-1 min-w-0" data-id="element-18904">
                <div className="flex justify-between items-baseline gap-2" data-id="element-18905">
                  <span className={`text-sm truncate ${m.unread > 0 ? 'font-bold text-slate-900' : 'font-medium text-slate-700'}`} data-id="element-18906">
                    {m.name}
                  </span>
                  <span className={`text-[10px] shrink-0 ${m.unread > 0 ? 'text-indigo-600 font-bold' : 'text-slate-400'}`} data-id="element-18907">
                    {m.time}
                  </span>
                </div>
                <div className={`text-xs truncate mt-0.5 ${m.unread > 0 ? 'text-slate-800' : 'text-slate-500'}`} data-id="element-18908">
                  {m.msg}
                </div>
              </div>
              <ChevronRight className="w-4 h-4 text-slate-300 shrink-0 hidden sm:block" data-id="element-18909" />
            </div>)}
        </div>
      </section>

      {/* ── 6. Астропрофиль ── */}
      <section className="space-y-2.5" data-id="element-18910">
        <SectionHeader title={t('userdash.overview.myAstroProfile')} tab="profile" linkText={t('userdash.overview.allProfiles')} data-id="element-18911" />
        <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm" data-id="element-18912">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-id="element-18913">
            <div className="flex items-center gap-3" data-id="element-18914">
              <div className={`w-11 h-11 rounded-full bg-gradient-to-br ${theme.gradient} flex items-center justify-center text-white text-lg shadow-sm shrink-0`} data-id="element-18915">
                {theme.icon}
              </div>
              <div data-id="element-18916">
                <div className="flex items-center gap-1.5" data-id="element-18917">
                  <span className="font-bold text-slate-900 text-sm" data-id="element-18918">
                    {user?.name || 'Мой профиль'}
                  </span>
                  <Badge variant="neutral" size="sm" className="text-[9px] py-0 h-3.5 px-1" data-id="element-18919">
                    {t('userdash.overview.primary')}
                  </Badge>
                </div>
                <div className="text-xs text-slate-500 mt-0.5" data-id="element-18920">
                  {theme.name} • 12 Апр 1990, 14:30 • Москва
                </div>
              </div>
            </div>
            <div className="flex gap-2 w-full sm:w-auto shrink-0" data-id="element-18921">
              <Button variant="secondary" size="sm" className="flex-1 sm:flex-none text-xs" onClick={() => showToast(t('userdash.overview.profileSelected'))} data-id="element-18922">
                {t('userdash.overview.use')}
              </Button>
              <Button variant="outline" size="sm" className="px-2.5" onClick={() => setActiveTab('profile')} title="Редактировать" data-id="element-18923">
                <UserIcon className="w-4 h-4" data-id="element-18924" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* ── 7. Покупки ── */}
      <section className="space-y-2.5" data-id="element-18925">
        <SectionHeader title={t('userdash.overview.recentPurchases')} tab="purchases" data-id="element-18926" />
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm divide-y divide-slate-100" data-id="element-18927">
          {purchases.map((p, i) => <div key={i} className="flex items-center justify-between px-4 py-3 hover:bg-slate-50 transition-colors" data-id="element-18928">
              <div className="flex items-center gap-3 min-w-0" data-id="element-18929">
                <div className="w-9 h-9 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center shrink-0" data-id="element-18930">
                  <p.Icon className="w-4 h-4" data-id="element-18931" />
                </div>
                <div className="min-w-0" data-id="element-18932">
                  <div className="font-semibold text-sm text-slate-900 truncate" data-id="element-18933">
                    {p.title}
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5" data-id="element-18934">{p.type}</div>
                </div>
              </div>
              <Button variant="ghost" size="sm" className={`text-xs font-medium ${theme.text} hover:bg-indigo-50 shrink-0 ml-2`} onClick={() => showToast(t('userdash.overview.opening', {
            title: p.title
          }))} data-id="element-18935">
                {t('userdash.overview.open')}
              </Button>
            </div>)}
        </div>
      </section>
    </div>;
}